import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api/bank-parsing' 
  : 'http://localhost:5001/api/bank-parsing';

/**
 * Get bank parsing field mappings
 */
export const getMappings = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/mappings`, {
      withCredentials: true
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching bank parsing mappings:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch mappings';
  }
};

/**
 * Save bank parsing field mappings
 */
export const saveMappings = async (mappings) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/mappings`, {
      mappings
    }, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.error || 'Failed to save mappings');
    }
  } catch (error) {
    console.error('Error saving bank parsing mappings:', error);
    throw error.response?.data?.error || error.message || 'Failed to save mappings';
  }
};

/**
 * Analyze bank statements (Phase 1: just upload and return success)
 */
export const analyze = async (files) => {
  try {
    const formData = new FormData();
    
    // Add all files to form data
    files.forEach((file) => {
      formData.append('statements', file);
    });
    
    const response = await axios.post(`${API_BASE_URL}/analyze`, formData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.error || 'Failed to analyze bank statements');
    }
  } catch (error) {
    console.error('Error analyzing bank statements:', error);
    throw error.response?.data?.error || error.message || 'Failed to analyze bank statements';
  }
};

/**
 * Verify business name from bank statement (pre-analysis step for HubSpot contacts)
 */
export const verifyBusinessName = async (files, applicationFiles = []) => {
  try {
    const formData = new FormData();
    
    // Handle both single file and multiple files
    const fileArray = Array.isArray(files) ? files : [files];
    
    // Append all files with the same field name 'statements'
    fileArray.forEach(file => {
      formData.append('statements', file);
    });
    
    // Append application files if present
    if (applicationFiles && Array.isArray(applicationFiles)) {
      applicationFiles.forEach(file => {
        formData.append('applicationFiles', file);
      });
    }
    
    const response = await axios.post(`${API_BASE_URL}/verify-business-name`, formData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.error || 'Failed to verify business name');
    }
  } catch (error) {
    console.error('Error verifying business name:', error);
    throw error.response?.data?.error || error.message || 'Failed to verify business name';
  }
};

/**
 * Generate and download PDF report
 */
export const downloadPdfReport = async (analysisData) => {
  try {
    console.log('[PDF Download] Starting download request...');
    
    const response = await axios.post(`${API_BASE_URL}/report/pdf`, analysisData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      },
      responseType: 'blob' // Important for binary data
    });
    
    console.log('[PDF Download] Response received:', {
      status: response.status,
      contentType: response.headers['content-type'],
      contentLength: response.headers['content-length'],
      dataSize: response.data.size
    });
    
    // Validate the response
    if (response.data.size === 0) {
      throw new Error('Received empty PDF file');
    }
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    console.log('[PDF Download] Blob created:', {
      blobSize: blob.size,
      blobType: blob.type
    });
    
    // Extract filename from response headers if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'bank-statement-report.pdf';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Create temporary link and click it to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
    
  } catch (error) {
    console.error('Error downloading PDF report:', error);
    throw error.response?.data?.error || error.message || 'Failed to generate PDF report';
  }
};
